import { useSyncExternalStore } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { shiftsApi } from "./api";
import type { Shift, ScheduledShift } from "./types";
import {
  SHIFT_API_SETTINGS_QUERY_KEY,
  type EmployeeShiftApiSettings,
} from "./employeeShiftApiSettings";
import { isTransportError } from "@/shared/utils/networkErrors";
import {
  enqueueShiftStart,
  enqueueShiftEnd,
  loadShiftActionOutbox,
} from "./offline/shiftActionOutbox";
import {
  buildProvisionalStartShift,
  buildProvisionalEndedShift,
} from "./offline/offlineShiftPlaceholders";
import { logger } from "@/shared/logger";

// Cache open shift for 2 minutes; we invalidate on start/end/update so this reduces redundant requests.
const OPEN_SHIFT_STALE_MS = 2 * 60 * 1000;
const OPEN_SHIFT_REFETCH_INTERVAL_MS = 2 * 60 * 1000;

export { SHIFT_API_SETTINGS_QUERY_KEY };

export function useShiftActionOutbox() {
  return useQuery({
    queryKey: ["shifts", "outbox"],
    queryFn: () => loadShiftActionOutbox(),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function useOpenShift() {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["shifts", "open"],
    queryFn: async () => {
      const { shift, settings } = await shiftsApi.getOpenShift();
      if (settings) {
        queryClient.setQueryData<EmployeeShiftApiSettings | undefined>(
          SHIFT_API_SETTINGS_QUERY_KEY,
          settings
        );
      }
      return shift;
    },
    staleTime: OPEN_SHIFT_STALE_MS,
    refetchOnWindowFocus: false,
    refetchInterval: OPEN_SHIFT_REFETCH_INTERVAL_MS,
    networkMode: "offlineFirst",
    gcTime: 24 * 60 * 60 * 1000,
  });
}

export function useShifts() {
  return useQuery({
    queryKey: ["shifts", "list"],
    queryFn: () => shiftsApi.getShifts(),
  });
}

/**
 * Fetch actual (worked) shifts whose start falls within [startDate, endDate).
 * Used alongside `useScheduledShifts` to surface unscheduled shifts in the
 * calendar view.
 */
export function useShiftsInRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined
) {
  return useQuery<Shift[]>({
    queryKey: ["shifts", "range", startDate, endDate],
    queryFn: () => shiftsApi.getShiftsByRange(startDate!, endDate!),
    enabled: !!startDate && !!endDate,
    staleTime: 60_000,
  });
}

/**
 * Fetch scheduled shifts overlapping [startDate, endDate). Both are ISO
 * date/datetime strings; `endDate` is exclusive per the API contract.
 * The query is disabled when either bound is missing.
 */
export function useScheduledShifts(
  startDate: string | null | undefined,
  endDate: string | null | undefined
) {
  return useQuery<ScheduledShift[]>({
    queryKey: ["shifts", "scheduled", startDate, endDate],
    queryFn: () => shiftsApi.getScheduledShifts(startDate!, endDate!),
    enabled: !!startDate && !!endDate,
    staleTime: 60_000,
  });
}

export function useStartShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      notes,
      manualArrivalTime,
      coords,
    }: {
      notes?: string;
      manualArrivalTime?: string;
      coords?: { latitude: number; longitude: number } | null;
    }) => {
      logger.info("shifts.start.request", {
        hasNotes: !!notes,
        hasManualArrival: !!manualArrivalTime,
        hasCoords: !!coords,
      });
      try {
        const { shift, settings } = await shiftsApi.startShift(
          notes,
          manualArrivalTime,
          coords
        );
        if (settings) {
          queryClient.setQueryData<EmployeeShiftApiSettings | undefined>(
            SHIFT_API_SETTINGS_QUERY_KEY,
            settings
          );
        }
        logger.info("shifts.start.apiSuccess", {
          shiftId: shift.id,
          startedAt: shift.started_at,
        });
        return shift;
      } catch (e) {
        logger.warn("shifts.start.apiError", { error: e });
        if (isTransportError(e)) {
          const user = queryClient.getQueryData<{ id?: number } | null>(["auth", "user"]);
          if (typeof user?.id !== "number") {
            throw e;
          }
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
          const clientRecordedAt = new Date().toISOString();
          await enqueueShiftStart({
            notes,
            manualArrivalTime,
            coords: coords ?? null,
            timezone: tz,
          });
          queryClient.invalidateQueries({ queryKey: ["shifts", "outbox"] });
          logger.info("shifts.start.queuedOffline", {
            employeeId: user.id,
          });
          return buildProvisionalStartShift(
            { notes, manualArrivalTime, coords: coords ?? null },
            user.id,
            clientRecordedAt
          );
        }
        throw e;
      }
    },
    onSuccess: (shift) => {
      logger.info("shifts.start.onSuccessCache", {
        shiftId: shift.id,
        offlinePending: !!shift.offlinePending,
      });
      // Set open shift from the start response so notes (and other fields) are in cache.
      // If we only invalidated, a refetched GET /shifts/open might not return notes
      // (e.g. backend bug), and the confirm-end page would show empty notes.
      queryClient.setQueryData(["shifts", "open"], shift);
      // Invalidate shifts list so the new shift appears in the list
      queryClient.invalidateQueries({ queryKey: ["shifts", "list"] });
    },
  });
}

export function useEndShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      notes,
      manualEndTime,
      endOfShiftChecklist,
      coords,
    }: {
      notes?: string;
      manualEndTime?: string;
      endOfShiftChecklist?: Array<{ item: string; completed: boolean }>;
      coords?: { latitude: number; longitude: number } | null;
    }) => {
      logger.info("shifts.end.request", {
        hasNotes: !!notes,
        hasManualEnd: !!manualEndTime,
        checklistCount: endOfShiftChecklist?.length ?? 0,
        hasCoords: !!coords,
      });
      try {
        const { shift, settings } = await shiftsApi.endShift(
          notes,
          manualEndTime,
          endOfShiftChecklist,
          coords
        );
        if (settings) {
          queryClient.setQueryData<EmployeeShiftApiSettings | undefined>(
            SHIFT_API_SETTINGS_QUERY_KEY,
            settings
          );
        }
        logger.info("shifts.end.apiSuccess", {
          shiftId: shift.id,
          endedAt: shift.ended_at,
        });
        return shift;
      } catch (e) {
        logger.warn("shifts.end.apiError", { error: e });
        if (isTransportError(e)) {
          await enqueueShiftEnd({
            notes,
            manualEndTime,
            endOfShiftChecklist,
            coords: coords ?? null,
          });
          const open = queryClient.getQueryData<Shift | null>(["shifts", "open"]);
          queryClient.invalidateQueries({ queryKey: ["shifts", "outbox"] });
          logger.info("shifts.end.queuedOffline");
          return buildProvisionalEndedShift(open ?? undefined, {
            notes,
            manualEndTime,
            endOfShiftChecklist,
            coords: coords ?? null,
          });
        }
        throw e;
      }
    },
    onSuccess: (shift) => {
      logger.info("shifts.end.onSuccessCache", {
        shiftId: shift.id,
        offlinePendingEnd: !!shift.offlinePendingEnd,
      });
      if (shift.offlinePendingEnd) {
        queryClient.setQueryData(["shifts", "open"], null);
      }
      // Invalidate and refetch open shift (should now be null)
      queryClient.invalidateQueries({ queryKey: ["shifts", "open"] });
      // Invalidate shifts list to show the ended shift
      queryClient.invalidateQueries({ queryKey: ["shifts", "list"] });
    },
  });
}

export function useChecklist(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["checklist"],
    queryFn: async () => {
      const result = await shiftsApi.getChecklist();
      if (result.settings) {
        queryClient.setQueryData<EmployeeShiftApiSettings | undefined>(
          SHIFT_API_SETTINGS_QUERY_KEY,
          result.settings
        );
      }
      return {
        checklist: result.checklist,
        shift: result.shift,
      };
    },
    enabled: options?.enabled !== false,
  });
}

/**
 * Subscribes to cached {@link EmployeeShiftApiSettings} populated by shift API hooks.
 */
export function useEmployeeShiftApiSettings(): EmployeeShiftApiSettings | undefined {
  const queryClient = useQueryClient();

  return useSyncExternalStore(
    (onStoreChange) => queryClient.getQueryCache().subscribe(onStoreChange),
    () =>
      queryClient.getQueryData<EmployeeShiftApiSettings | undefined>(
        SHIFT_API_SETTINGS_QUERY_KEY
      ),
    () => undefined
  );
}

export function useUpdateShiftNotes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      shiftId,
      notes,
      endOfShiftChecklist,
    }: {
      shiftId: number;
      notes: string;
      endOfShiftChecklist?: Array<{ item: string; completed: boolean }>;
    }) =>
      shiftsApi.updateShiftNotes(shiftId, notes, endOfShiftChecklist),
    onSuccess: () => {
      // Invalidate both open shift and shifts list
      queryClient.invalidateQueries({ queryKey: ["shifts", "open"] });
      queryClient.invalidateQueries({ queryKey: ["shifts", "list"] });
    },
  });
}

